/**
 * How an in-app (Agora) call ends on the PCA's side.
 *
 * Two bugs on staging (Oct 2026):
 *   - When the call ended any way other than the red end-call button (e.g. the patient's app
 *     went to the background and Agora dropped them), callLogs/{chat} kept status "in_call" and
 *     hostStatus "available". The patient app could then rejoin the dead call from the chat
 *     message for up to an hour (the token's life) and believe the PCA was there.
 *   - Agora reports a remote user as left both when they hang up ("Quit") and when their app
 *     goes silent for ~20s ("ServerTimeOut", e.g. the patient's app in the background). Both
 *     ended the PCA's call at once, while the patient's side reconnected to an empty channel.
 */

// Statuses that already mean the call is over; they are never overwritten.
const ENDED_STATUSES = new Set(["doctor_ended", "patient_ended", "declined", "cancelled"]);

/**
 * The callLogs updates that mark the call ended, as the red end-call button writes them
 * (Controls.leaveChannel). A status the patient or an earlier step already ended with is kept;
 * hostStatus always becomes "unavailable" so the patient app cannot rejoin.
 */
export const callEndedUpdates = (conversationId, currentStatus, now = Date.now()) => {
    if (!conversationId) return null;
    const base = `callLogs/${conversationId}`;
    const updates = {[`${base}/hostStatus`]: "unavailable"};
    if (!ENDED_STATUSES.has(currentStatus)) {
        updates[`${base}/status`] = "doctor_ended";
        updates[`${base}/events/${now}`] = "doctor_ended";
    }
    return updates;
};

/** Writes callEndedUpdates for the chat. Never throws: ending the call must not fail on it. */
export const markInAppCallEnded = async (database, conversationId, now = Date.now()) => {
    if (!conversationId) return;
    try {
        const snap = await database.ref(`callLogs/${conversationId}/status`).once("value");
        const updates = callEndedUpdates(conversationId, snap.val(), now);
        if (updates) await database.ref().update(updates);
    } catch (e) {
        console.error("Could not mark the in-app call ended:", e);
    }
};

// How long the PCA waits for a patient whose app went silent before the call is ended.
export const RECONNECT_GRACE_MS = 45000;

/** True when Agora dropped the remote user for going silent, not because they hung up. */
export const isTimeoutLeave = (reason) => reason === "ServerTimeOut";

/**
 * Decides what a remote "user-left" does: a hang-up ends the call now; a time-out waits
 * RECONNECT_GRACE_MS for the patient to come back (onReconnecting(true) while waiting) and ends
 * it only if they don't. userBack() cancels the wait when they rejoin; dispose() on unmount.
 */
export const createRemoteLeaveHandler = ({
    endCall,
    onReconnecting = () => {},
    graceMs = RECONNECT_GRACE_MS,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
}) => {
    let timer = null;
    const cancel = () => {
        if (timer !== null) {
            clearTimer(timer);
            timer = null;
        }
    };
    return {
        userLeft(reason) {
            if (!isTimeoutLeave(reason)) {
                cancel();
                onReconnecting(false);
                endCall(reason || "remote_left");
                return;
            }
            if (timer !== null) return; // already waiting
            onReconnecting(true);
            timer = setTimer(() => {
                timer = null;
                onReconnecting(false);
                endCall("remote_timeout");
            }, graceMs);
        },
        userBack() {
            if (timer === null) return;
            cancel();
            onReconnecting(false);
        },
        dispose: cancel,
        isWaiting: () => timer !== null,
    };
};
